import type {
  StorePlanningCoverageKind,
  StorePlanningDay,
  StorePlanningShift,
  StorePlanningTask,
  StoreProcessTaskType,
} from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useState } from 'react'
import {
  CleanIcon,
  MessageMultipleIcon,
  PackageDeliveredIcon,
  ShelvingUnitIcon,
  Truck24Icon,
} from '../icons/dashboard-icons'
import { TaskCompletionModal } from '../TaskCompletionModal'
import {
  AFFLUENCE_WIDTH,
  affluenceCellStyle,
  CLOCK_COLOR,
  CLOCK_LABEL,
  COVERAGE_COLOR,
  COVERAGE_WIDTH,
  DAY_LABELS,
  GRID_TOP,
  minutesOf,
  ROW_COUNT,
  ROW_HEIGHT,
  TASK_THEME,
  yOf,
} from './planning-constants'
import {
  ClockGlyph,
  ComputerUserIcon,
  DoorIcon,
  LocationUserIcon,
  TruckDeliveryIcon,
} from './planning-icons'
import { assignLanes, laneRight, laneWidth, shiftCardBounds } from './planning-layout'

const COVERAGE_ICON: Record<StorePlanningCoverageKind, typeof LocationUserIcon> = {
  presentiel: LocationUserIcon,
  distanciel: ComputerUserIcon,
  ouverture: DoorIcon,
}

// Icône par type de tâche « process » (store_process_tasks), pour le bloc du planning.
const TASK_ICON: Record<StoreProcessTaskType, typeof CleanIcon> = {
  CLEANING: CleanIcon,
  RECEPTION: PackageDeliveredIcon,
  LIVRAISON: Truck24Icon,
  INVENTORY: ShelvingUnitIcon,
  CLIENT_RELATION: MessageMultipleIcon,
}

// En dessous, la carte n'a de place que pour l'avatar et l'heure de début.
const MIN_HEIGHT_FOR_END_LABEL = 84

// Une pastille d'icône n'a de sens que si son segment est assez haut pour l'accueillir.
const COVERAGE_ICON_MIN_HEIGHT = 30

function ShiftCard({
  shift,
  lane,
  laneCount,
  rowHeight,
}: {
  shift: StorePlanningShift
  lane: number
  laneCount: number
  rowHeight: number
}) {
  const { top, height } = shiftCardBounds(shift, rowHeight)

  return (
    // Sous 40 px de large (nombreuses lanes), les heures passent en 8 px (container query) et
    // overflow-hidden rogne le reste plutôt que de le laisser déborder sur la carte voisine.
    <div
      className="@container absolute overflow-hidden rounded-[8px] border border-[#BFBEB1] bg-[#F0EDE2]"
      style={{ top, height, width: laneWidth(laneCount), right: laneRight(lane, laneCount) }}
      title={`${shift.collaboratorName} · ${shift.startTime} – ${shift.endTime} · ${CLOCK_LABEL[shift.clockStatus]}`}
    >
      {shift.profilePictureUrl ? (
        <img
          src={shift.profilePictureUrl}
          alt=""
          className="absolute top-px left-px h-[40px] w-full rounded-t-[7px] object-cover"
        />
      ) : (
        <span className="absolute top-px left-px flex h-[40px] w-full items-center justify-center rounded-t-[7px] bg-[#CEE3C4] text-[12px] font-semibold text-[#354A31]">
          {shift.collaboratorName
            .split(' ')
            .map((part) => part[0])
            .join('')
            .slice(0, 2)}
        </span>
      )}

      <span
        className="absolute top-[29px] left-1/2 flex -translate-x-1/2 items-center rounded-full bg-[#F0EDE2] p-[3px]"
        style={{ color: CLOCK_COLOR[shift.clockStatus] }}
      >
        <ClockGlyph size={18} />
      </span>

      <span className="absolute top-[52px] left-0 w-full text-center font-sans text-[10px] font-medium @max-[30px]:text-[8px] text-[#152013] opacity-70">
        {shift.clockedInAt ?? shift.startTime}
      </span>

      {/* Sous cette hauteur, le trait et l'heure de fin viendraient chevaucher l'heure de début. */}
      {height >= MIN_HEIGHT_FOR_END_LABEL ? (
        <>
          <span
            className="absolute left-1/2 w-px bg-[#BFBEB1]"
            style={{ top: 68, height: Math.max(0, height - 68 - 18) }}
          />
          <span className="absolute bottom-[2px] left-0 w-full text-center font-sans text-[10px] font-medium @max-[30px]:text-[8px] text-[#152013] opacity-70">
            {shift.clockedOutAt ?? shift.endTime}
          </span>
        </>
      ) : null}
    </div>
  )
}

export function PlanningDayColumn({ day }: { day: StorePlanningDay }) {
  // Grille à taille réelle : le défilement vertical du widget remplace l'ancien écrasement des
  // lignes (cf. `PlanningWeekCard`).
  const rowHeight = ROW_HEIGHT
  const lanes = assignLanes(day.shifts, rowHeight)
  const [selectedTask, setSelectedTask] = useState<StorePlanningTask | null>(null)

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* En-tête de jour épinglé : reste visible pendant le défilement des heures. Le padding bas
          (avec `box-content`) prolonge le fond sur les 8 px qui séparent l'en-tête de la grille. */}
      <div className="sticky top-0 z-10 box-content flex h-[25px] items-center gap-[8px] bg-[#F0EDE2] pb-[8px]">
        <span
          className="shrink-0 font-sans text-[10px] text-[#152013] opacity-60"
          style={{ width: AFFLUENCE_WIDTH }}
        >
          ca
        </span>
        <div
          className={cn(
            'flex h-full min-w-0 flex-1 items-center justify-center rounded-[6px] font-sans text-[11px] font-semibold',
            day.isToday
              ? 'bg-[#B8D9A8] text-[#152013]'
              : 'border border-[#0000000F] bg-[#F9F8F1] text-[#152013]',
          )}
        >
          {DAY_LABELS[day.weekday - 1]}
        </div>
      </div>

      {/* shrink-0 : sinon la colonne (étirée à la hauteur du scroller) écrase ce corps sous la
          hauteur réelle de la grille et le fond #F9F8F1 ne descend pas jusqu'en bas. */}
      <div className="flex shrink-0 gap-[4px]" style={{ height: GRID_TOP + ROW_COUNT * rowHeight }}>
        <div className="relative shrink-0" style={{ width: AFFLUENCE_WIDTH }}>
          {day.affluence.map((value, index) => (
            <span
              key={index}
              className="absolute flex items-center justify-center rounded-[4px] font-sans text-[10px] font-medium"
              style={{
                top: GRID_TOP + index * rowHeight,
                height: rowHeight - 1,
                width: AFFLUENCE_WIDTH,
                ...affluenceCellStyle(value),
              }}
            >
              {value}
            </span>
          ))}
        </div>

        <div className="relative shrink-0" style={{ width: COVERAGE_WIDTH }}>
          {day.coverage.map((segment) => {
            const top = yOf(minutesOf(segment.startTime), rowHeight)
            const height = yOf(minutesOf(segment.endTime), rowHeight) - top
            if (height <= 0) return null
            return (
              <span
                key={`${segment.kind}-${segment.startTime}`}
                className="absolute w-full rounded-[2px]"
                style={{ top, height, backgroundColor: COVERAGE_COLOR[segment.kind] }}
              />
            )
          })}
        </div>

        <div className="relative min-w-0 flex-1 rounded-[4px] bg-[#F9F8F1]">
          {Array.from({ length: ROW_COUNT + 1 }, (_, index) => (
            <span
              key={index}
              className="absolute left-0 h-px w-full bg-[#0000000F]"
              style={{ top: GRID_TOP + index * rowHeight }}
            />
          ))}

          {day.coverage.map((segment) => {
            const top = yOf(minutesOf(segment.startTime), rowHeight)
            const height = yOf(minutesOf(segment.endTime), rowHeight) - top
            if (height < COVERAGE_ICON_MIN_HEIGHT) return null
            const Icon = COVERAGE_ICON[segment.kind]
            return (
              <span
                key={`icon-${segment.kind}-${segment.startTime}`}
                className="absolute flex size-[26px] items-center justify-center rounded-r-[4px] rounded-l-[2px] text-[#F9F8F1]"
                style={{ top, left: -10, backgroundColor: COVERAGE_COLOR[segment.kind] }}
              >
                <Icon />
              </span>
            )
          })}

          {day.events.map((event) => {
            const top = yOf(minutesOf(event.startTime), rowHeight)
            const height = Math.max(16, yOf(minutesOf(event.endTime), rowHeight) - top)
            return (
              <div
                key={event.id}
                className="absolute left-0 flex w-full gap-[4px] overflow-hidden rounded-l-[4px] bg-[#FCEED9]"
                style={{ top, height }}
              >
                <span className="h-full w-[6px] shrink-0 bg-[#EFA031]" />
                <div className="flex min-w-0 flex-col gap-[4px] py-[2px]">
                  <TruckDeliveryIcon className="text-[#EFA031]" />
                  <span className="font-sans text-[8px] leading-[10px] font-semibold text-[#152013]">
                    {event.label}
                  </span>
                  <span className="font-sans text-[8px] leading-[10px] font-medium text-[#6A9560]">
                    {event.startTime}
                  </span>
                </div>
              </div>
            )
          })}

          {day.tasks.map((task) => {
            const top = yOf(minutesOf(task.startTime), rowHeight)
            const height = Math.max(16, yOf(minutesOf(task.endTime), rowHeight) - top)
            const Icon = TASK_ICON[task.type]
            const theme = TASK_THEME[task.type]
            const hasIssue = task.completionStatus === 'WITH_ISSUES'
            return (
              <button
                key={`task-${task.id}`}
                type="button"
                onClick={() => setSelectedTask(task)}
                className={cn(
                  'absolute left-0 flex w-full cursor-pointer gap-[4px] overflow-hidden rounded-[4px] text-left',
                  task.completed && 'opacity-60',
                )}
                style={{ top, height, backgroundColor: theme.bg }}
                title={`${task.label} · ${task.startTime} - ${task.endTime}${task.completed ? ' · fait' : ''}${hasIssue ? ` · Problème signalé${task.issueComment ? ` : ${task.issueComment}` : ''}` : ''}`}
              >
                <span className="h-full w-[6px] shrink-0" style={{ backgroundColor: theme.bar }} />
                {/* Pastille « problème signalé » : la couverture ne masque pas l'anomalie. */}
                {hasIssue ? (
                  <span className="absolute top-[2px] right-[2px] size-[6px] rounded-full bg-[#EF8031]" />
                ) : null}
                <div
                  className="flex min-w-0 flex-col gap-[4px] py-[2px]"
                  style={{ color: theme.text }}
                >
                  <Icon className="size-[12px]" />
                  <span
                    className={cn(
                      'font-sans text-[8px] leading-[10px] font-semibold text-[#152013]',
                      task.completed && 'line-through',
                    )}
                  >
                    {task.label}
                  </span>
                  <span
                    className="font-sans text-[8px] leading-[10px] font-medium"
                    style={{ color: theme.text }}
                  >
                    {task.startTime} - {task.endTime}
                  </span>
                </div>
              </button>
            )
          })}

          {day.shifts.map((shift) => (
            <ShiftCard
              key={shift.id}
              shift={shift}
              lane={lanes.get(shift.id)?.lane ?? 0}
              laneCount={lanes.get(shift.id)?.laneCount ?? 1}
              rowHeight={rowHeight}
            />
          ))}
        </div>
      </div>

      {selectedTask ? (
        <TaskCompletionModal
          task={selectedTask}
          shifts={day.shifts}
          onClose={() => setSelectedTask(null)}
        />
      ) : null}
    </div>
  )
}
